(() => {
  const ROOT = window.Shopify?.routes?.root ? Shopify.routes.root : '/';
  const LOCK = '__asp_pairs_d_lock__';
  const i18n = () => window.aspPairsI18n || {};

  const qs = (s, el = document) => el.querySelector(s);
  const qsa = (s, el = document) => Array.from(el.querySelectorAll(s));

  const enablePairsWell = () => {
    window.__ASP_PAIRS__ = true;
    document.documentElement.classList.add('asp-pairs-on');
    document.documentElement.dataset.aspPairs = '1';
    try {
      document.dispatchEvent(new CustomEvent('asp:pairs-enabled'));
    } catch (e) {}
  };

  window.aspEnablePairsWell = enablePairsWell;

  const isOn = () =>
    window.__ASP_PAIRS__ === true ||
    document.documentElement.classList.contains('asp-pairs-on') ||
    document.documentElement.dataset.aspPairs === '1';

  const root = () => qs('[data-asp-pairs-root]');

  // A/B 2026-11: arm A = makeup bag (default), arm B = set of 3 mystery charms.
  // Intelligems group B sets html[data-asp-bundle-arm="charms"]; rows of the other arm are hidden and skipped.
  const arm = () => (document.documentElement.getAttribute('data-asp-bundle-arm') === 'charms' ? 'charms' : 'bag');

  const inArm = (card) => {
    const a = card.getAttribute('data-asp-bundle-arm');
    return !a || a === arm();
  };

  const cards = () => qsa('[data-asp-pairs-card]', root() || document).filter(inArm);

  const testItemCard = () => cards().find((card) => card.getAttribute('data-asp-bundle-arm') === arm());

  // Same event name goes to Intelligems (custom JS event), Clarity, GA dataLayer and Shopify custom pixels.
  const sent = {};
  const track = (name, once) => {
    if (once) {
      if (sent[name]) return;
      sent[name] = true;
    }
    const a = arm();
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: name });
    } catch (e) {}
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'asp_bundle_arm', a);
        window.clarity('event', name);
      }
    } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, asp_bundle_arm: a });
    } catch (e) {}
    try {
      window.Shopify?.analytics?.publish?.(name, { asp_bundle_arm: a });
    } catch (e) {}
  };

  const trackAdd = () => {
    track('asp_bundle_add', true);
    const item = testItemCard();
    if (item && isChecked(item)) track('asp_bundle_add_test_item', true);
  };

  let viewObserver = null;
  const watchView = () => {
    const r = root();
    if (!r || viewObserver || sent.asp_bundle_view || !('IntersectionObserver' in window)) return;
    viewObserver = new IntersectionObserver(
      (entries) => {
        const seen = entries.some(
          (en) => en.isIntersecting && (en.intersectionRatio >= 0.3 || en.intersectionRect.height >= 300)
        );
        if (!seen) return;
        track('asp_bundle_view', true);
        viewObserver.disconnect();
      },
      { threshold: [0.1, 0.2, 0.3] }
    );
    viewObserver.observe(r);
  };

  const groupAmount = (cents, decimals, thousands, decimal) => {
    const [whole, fraction] = ((Number(cents) || 0) / 100).toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };

  const money = (cents) => {
    const settings = window.themeVariables?.settings;
    const format = settings && (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat);
    if (!format) {
      if (typeof window.aspFormatMoney === 'function') return window.aspFormatMoney(cents);
      return groupAmount(cents, 2, ',', '.');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return groupAmount(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return groupAmount(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return groupAmount(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return groupAmount(cents, 2, "'", '.');
        case 'amount_with_space_separator':
          return groupAmount(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return groupAmount(cents, 0, ' ', ',');
        default:
          return groupAmount(cents, 2, ',', '.');
      }
    });
  };

  const usable = (v) => {
    if (v == null || v === '') return false;
    if (typeof File !== 'undefined' && v instanceof File) return v.size > 0 && !!v.name;
    return true;
  };

  const fetchJSON = async (url, opts = {}) => {
    const headers = { Accept: 'application/json', ...(opts.headers || {}) };
    if (opts.body && !(opts.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, { credentials: 'same-origin', ...opts, headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const addCartItems = async (items) => {
    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id));
      formData.append(`items[${index}][quantity]`, String(item.quantity));
      if (item.selling_plan) formData.append(`items[${index}][selling_plan]`, String(item.selling_plan));
      if (!item.properties) return;
      Object.entries(item.properties).forEach(([key, value]) => {
        if (!usable(value)) return;
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    });
    return fetchJSON(`${ROOT}cart/add.js`, { method: 'POST', body: formData });
  };

  const extractMain = (form) => {
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return null;
    const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;
    const properties = {};
    for (const [k, v] of fd.entries()) {
      if (!k.startsWith('properties[')) continue;
      const name = k.slice('properties['.length, -1);
      if (!usable(v)) continue;
      properties[name] = v;
    }
    const item = { id: Number(id), quantity };
    if (Object.keys(properties).length) item.properties = properties;
    const sellingPlan = fd.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;
    return item;
  };

  const forProductValue = (form) => {
    const productTitle = (qs('h1')?.textContent || '').trim();
    let selectedOptions = [];
    const formId = form.getAttribute('id') || form.id;
    if (formId) {
      const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
      if (picker) {
        selectedOptions = Array.from(picker.querySelectorAll('[data-selected-option-value]'))
          .map((el) => (el.textContent || '').trim())
          .filter(Boolean);
      }
    }
    if (productTitle && selectedOptions.length) return `${productTitle} - ${selectedOptions.join(' / ')}`;
    return productTitle || '';
  };

  const checkbox = (card) => qs('[data-asp-pairs-check]', card);

  const isChecked = (card) => {
    const box = checkbox(card);
    return !!(box && box.checked && !box.disabled);
  };

  const cardQty = (card) => Math.max(1, Number(card.getAttribute('data-asp-pair-qty-min')) || 1);

  const selectedCards = () => cards().filter(isChecked);

  const cardItem = (card) => {
    const id = Number(card.getAttribute('data-asp-pair-vid') || 0);
    if (!Number.isFinite(id) || id <= 0) return null;
    return { id, quantity: cardQty(card), title: card.getAttribute('data-asp-pair-title') || '' };
  };

  const isUiOnly = (card) => card.hasAttribute('data-asp-pair-ui');

  const selectedItems = () =>
    selectedCards()
      .filter((card) => !isUiOnly(card))
      .map(cardItem)
      .filter(Boolean);

  const openCartOnce = async () => {
    const cart = await fetchJSON(`${ROOT}cart.js`);
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
    return cart;
  };

  const journalDiscountPct = () => {
    const el = document.querySelector('[data-paper-journal-discount]');
    const n = el ? Number(el.getAttribute('data-paper-journal-discount')) : 10;
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(90, n);
  };

  const discountedCents = (cents) => {
    const pct = journalDiscountPct();
    const value = Number(cents) || 0;
    if (!pct) return value;
    return Math.round((value * (100 - pct)) / 100);
  };

  const persoRow = () => qs('[data-asp-bundle-perso]', root() || document);

  const persoOn = () => !!persoRow()?.querySelector('[data-asp-bundle-perso-check]')?.checked;

  const renderTotals = () => {
    const r = root();
    if (!r) return;
    const main = Number(r.getAttribute('data-asp-bundle-main-price')) || 0;
    let was = main;
    let today = main;
    if (persoOn()) {
      const persoPrice = Number(persoRow().getAttribute('data-asp-pair-price')) || 0;
      was += persoPrice;
      today += persoPrice;
    }
    selectedCards().forEach((card) => {
      const price = Number(card.getAttribute('data-asp-pair-price')) || 0;
      const qty = cardQty(card);
      was += price * qty;
      if (!isUiOnly(card)) today += discountedCents(price) * qty;
    });
    qsa('[data-asp-bundle-gift]', r).forEach((row) => {
      const box = qs('[data-asp-bundle-gift-check]', row);
      if (box && !box.checked) return;
      was += Number(row.getAttribute('data-asp-pair-price')) || 0;
    });
    const separateEl = qs('[data-asp-bundle-separate]', r);
    const togetherEl = qs('[data-asp-bundle-together]', r);
    const saveEl = qs('[data-asp-bundle-save]', r);
    const sum = qs('[data-asp-bundle-sum]', r);
    if (separateEl) separateEl.textContent = money(was);
    if (togetherEl) togetherEl.textContent = money(today);
    if (saveEl) saveEl.textContent = money(was - today);
    const submitPrice = qs('[data-asp-bundle-submit-price]', r);
    if (submitPrice) submitPrice.textContent = money(today);
    if (sum) sum.hidden = was === today;
    qsa('[data-asp-bundle-thumb]', r).forEach((thumb) => {
      const pid = thumb.getAttribute('data-asp-bundle-thumb');
      if (pid === 'main') return;
      if (pid === 'perso') {
        thumb.classList.toggle('is-off', !persoOn());
        return;
      }
      const card = cards().find((c) => c.getAttribute('data-asp-pair-pid') === pid);
      thumb.classList.toggle('is-off', !card || !isChecked(card));
    });
  };

  const applyOption = (card, option) => {
    if (!option || !card) return;
    const vid = option.getAttribute('data-asp-pair-vid');
    const price = option.getAttribute('data-asp-pair-price');
    const src = option.getAttribute('data-asp-pair-src');
    const qtyMin = option.getAttribute('data-asp-pair-qty-min');
    if (vid) card.setAttribute('data-asp-pair-vid', vid);
    if (qtyMin) card.setAttribute('data-asp-pair-qty-min', qtyMin);
    if (price != null) {
      const cents = Number(price) || 0;
      card.setAttribute('data-asp-pair-price', String(cents));
      const compareEl = qs('[data-asp-pair-compare-money]', card);
      const saleEl = qs('[data-asp-pair-sale-money]', card);
      const qty = cardQty(card);
      if (compareEl) compareEl.textContent = money(cents * qty);
      if (saleEl) saleEl.textContent = money(discountedCents(cents) * qty);
    }
    const thumbImg = qs(`[data-asp-bundle-thumb="${card.getAttribute('data-asp-pair-pid')}"] img`, root() || document);
    if (thumbImg && src) {
      thumbImg.src = src;
      thumbImg.removeAttribute('srcset');
      thumbImg.removeAttribute('sizes');
    }
    const label = qs('[data-asp-pair-color-label]', card);
    if (label) label.textContent = option.getAttribute('data-asp-pair-color') || '';
    const input = qs('input', option);
    if (input) input.checked = true;
  };

  const rgbOf = (el) => (el?.getAttribute('data-asp-pair-rgb') || '').split(',').filter(Boolean).map(Number);
  const rgbDiff = (a, b) =>
    a.length === 3 && b.length === 3
      ? Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2])
      : Infinity;

  const matchColor = (color) => {
    if (!color) return;
    const journal = qsa('[data-asp-bundle-journal-color]', root() || document).find(
      (el) => el.getAttribute('data-asp-bundle-journal-color') === color
    );
    const journalRgb = rgbOf(journal);
    cards().forEach((card) => {
      const options = qsa('[data-asp-pair-swatch]', card);
      const wrap = qs('.asp-bundle__swatches', card);
      if (!options.length) return;
      const colorOf = (o) => o.getAttribute('data-asp-pair-color') || '';
      const exact = options.find((o) => colorOf(o) === color);
      const label = qs('[data-asp-pair-color-label]', card);
      if (exact) {
        card.removeAttribute('data-asp-pair-picked');
        if (wrap && !wrap.hasAttribute('data-asp-pair-swatches-always')) wrap.hidden = true;
        if (label) label.hidden = true;
        applyOption(card, exact);
        return;
      }
      if (wrap) wrap.hidden = false;
      if (label) label.hidden = false;
      if (card.hasAttribute('data-asp-pair-picked')) return;
      let near = null;
      let nearDiff = Infinity;
      options.forEach((o) => {
        const diff = rgbDiff(journalRgb, rgbOf(o));
        if (diff < nearDiff) {
          nearDiff = diff;
          near = o;
        }
      });
      const pick =
        near ||
        options.find((o) => colorOf(o) && color.indexOf(colorOf(o)) !== -1) ||
        options[0];
      applyOption(card, pick);
    });
  };

  const showBundleMsg = (text) => {
    const r = root();
    const el = r && qs('[data-asp-pairs-bundle-msg]', r);
    if (!el) return;
    el.hidden = !text;
    el.textContent = text || '';
  };

  const syncInCartState = async () => {};

  const getPaperWrap = (form) =>
    form?.closest('product-form')?.closest('.shopify-section, section, main, body')?.querySelector('[data-paper-addon]') ||
    qs('[data-paper-addon]');

  const paperIsValid = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap) return true;
    if (wrap.getAttribute('data-paper-mode') === 'mix') {
      const mode = (wrap.dataset.paperModeState || '').trim();
      if (mode === 'without') return true;
      if (mode !== 'with') return false;
      return wrap.dataset.paperMixOk === '1';
    }
    if (wrap.getAttribute('data-paper-mode') === 'types') {
      const mode = (wrap.dataset.paperModeState || '').trim();
      if (mode === 'without') return true;
      if (mode !== 'with') return false;
      return !!(wrap.dataset.selectedPaperVariantId || wrap.getAttribute('data-selected-paper-variant-id'));
    }
    const checked = wrap.querySelector('[data-paper-choice]:checked');
    if (!checked) return false;
    if (wrap.dataset.paperMulticolorOk === '0') return false;
    return true;
  };

  const paperExtras = (form) => {
    const items = [];
    const wrap = getPaperWrap(form);
    if (!wrap) return items;
    const forProduct = forProductValue(form);
    if (wrap.getAttribute('data-paper-mode') === 'mix') {
      if ((wrap.dataset.paperModeState || '').trim() !== 'with') return items;
      if (typeof window.aspGetPaperMixGroupedItems === 'function') {
        const mixLabel = typeof window.aspGetMixOrderedLabel === 'function' ? window.aspGetMixOrderedLabel(form) : '';
        const mixProp = typeof window.aspGetMixPropertyName === 'function' ? window.aspGetMixPropertyName(form) : 'Insert mix';
        window.aspGetPaperMixGroupedItems(form).forEach((g) => {
          const properties = { 'For Product': forProduct };
          if (mixLabel) properties[mixProp] = mixLabel;
          items.push({ id: g.id, quantity: g.quantity, properties });
        });
      }
      return items;
    }
    if (typeof window.aspGetSelectedPaperVariantId === 'function') {
      const id = window.aspGetSelectedPaperVariantId(form);
      if (id) {
        const qty = typeof window.aspGetPaperQty === 'function' ? window.aspGetPaperQty(form, 1) : 1;
        const properties = { 'For Product': forProduct };
        items.push({ id, quantity: qty, properties });
      }
    }
    return items;
  };

  const acquire = () => {
    const now = Date.now();
    const cur = window[LOCK];
    if (cur && now - cur < 1800) return false;
    window[LOCK] = now;
    return true;
  };

  const release = () => {
    window[LOCK] = 0;
  };

  const pairLines = (bundleToken, forProduct) =>
    selectedItems()
      .reverse()
      .map((p) => ({
        id: p.id,
        quantity: p.quantity,
        properties: {
          'For Product': forProduct,
          _bundle_token: bundleToken,
          _linked_child: 'pairs',
          _asp_pairs_well: '1',
          _asp_bundle_arm: arm()
        }
      }));

  let quizPending = false;

  const openQuiz = () => {
    const quizId = persoRow()?.getAttribute('data-asp-bundle-quiz');
    const modal = quizId && document.getElementById(quizId);
    const opener = quizId && document.querySelector(`.personalization-button[aria-controls="${CSS.escape(quizId)}"]`);
    if (!modal || !opener) return false;
    if (modal.dataset.aspBundleBound !== '1') {
      modal.dataset.aspBundleBound = '1';
      modal.addEventListener('dialog:after-hide', () => {
        quizPending = false;
      });
    }
    quizPending = true;
    opener.click();
    window.setTimeout(() => {
      if (!modal.hasAttribute('open')) quizPending = false;
    }, 800);
    return true;
  };

  window.aspBundleQuizItems = (bundleToken, forProduct) => {
    if (!quizPending || !isOn() || !root()) return [];
    const lines = pairLines(bundleToken, forProduct);
    if (lines.length) trackAdd();
    return lines;
  };

  const handleSubmit = async (form) => {
    if (!isOn() || !root()) return false;
    const pairs = selectedItems();
    if (!pairs.length) return false;
    if (!form.checkValidity()) {
      form.reportValidity();
      return true;
    }
    if (!paperIsValid(form)) {
      form.dispatchEvent(new CustomEvent('paper:multicolor-invalid', { bubbles: true }));
      form.dispatchEvent(new CustomEvent('paper:mix-invalid', { bubbles: true }));
      const wrap = getPaperWrap(form);
      wrap?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    }
    if (!acquire()) return true;

    const main = extractMain(form);
    if (!main) {
      release();
      return false;
    }

    try {
      const forProduct = forProductValue(form);
      const bundleToken = `pairs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mainProperties = { ...(main.properties || {}) };
      mainProperties._bundle_token = bundleToken;
      mainProperties._linked_parent = 'main';
      main.properties = mainProperties;

      const items = pairLines(bundleToken, forProduct);

      if (typeof window.aspSpareElasticLine === 'function') {
        const spare = window.aspSpareElasticLine(bundleToken, forProduct);
        if (spare) items.push(spare);
      }

      paperExtras(form).forEach((p) => {
        const properties = { ...(p.properties || {}), _bundle_token: bundleToken, 'For Product': forProduct };
        items.push({
          id: p.id,
          quantity: p.quantity,
          properties
        });
      });

      items.push(main);

      try {
        await addCartItems(items);
        trackAdd();
      } catch (err) {
        try {
          await addCartItems([main]);
          await openCartOnce();
          showBundleMsg(i18n().partialError || 'Journal added. One or more add-ons could not be added.');
        } catch (_) {}
        return true;
      }

      await openCartOnce();
      await syncInCartState();

      showBundleMsg('');
      return true;
    } finally {
      release();
    }
  };

  const isBundleSubmit = (e) => !!e.submitter?.matches?.('[data-asp-bundle-submit]');

  const onSubmitCapture = (e) => {
    if (!isOn() || !root()) return;
    const form = e.target;
    if (!form || form.tagName !== 'FORM' || !form.action || form.action.indexOf('/cart/add') === -1) return;
    if (!form.closest('product-form')) return;
    if (!isBundleSubmit(e)) return;
    track('asp_bundle_click', true);
    if (persoOn()) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      openQuiz();
      return;
    }
    if (!selectedItems().length) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (form.dataset.aspPairsBusy === '1') return;
    form.dataset.aspPairsBusy = '1';
    handleSubmit(form)
      .then((handled) => {
        if (handled === false) form.requestSubmit();
      })
      .catch(() => form.requestSubmit())
      .finally(() => {
        form.dataset.aspPairsBusy = '0';
      });
  };

  const onVariantChange = (e) => {
    const r = root();
    const variant = e.detail?.variant;
    if (!r || !variant) return;
    const button = qs('[data-asp-bundle-submit]', r);
    const formRef = e.detail.formId;
    const formId = typeof formRef === 'string' ? formRef : formRef?.form?.getAttribute('id');
    if (formId && button && button.getAttribute('form') !== formId) return;
    r.setAttribute('data-asp-bundle-main-price', String(Number(variant.price) || 0));
    const mainMoney = qs('[data-asp-bundle-main-money]', r);
    if (mainMoney) mainMoney.textContent = money(variant.price);
    const src = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
    const thumbImg = qs('[data-asp-bundle-thumb="main"] img', r);
    if (thumbImg && src) {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', '240');
      thumbImg.src = url.toString();
      thumbImg.removeAttribute('srcset');
      thumbImg.removeAttribute('sizes');
    }
    if (button) button.disabled = !variant.available;
    const colorIndex = r.getAttribute('data-asp-bundle-color-index');
    if (colorIndex != null && Array.isArray(variant.options)) matchColor(variant.options[Number(colorIndex)]);
    renderTotals();
    syncInCartState();
  };

  const bindAll = () => {
    renderTotals();
    syncInCartState();
    watchView();
  };

  document.addEventListener('submit', onSubmitCapture, true);

  document.addEventListener('change', (e) => {
    const r = root();
    if (r && r.contains(e.target)) {
      track('asp_bundle_interact', true);
      const item = testItemCard();
      if (item && e.target === checkbox(item) && !e.target.checked) track('asp_bundle_test_item_off', true);
    }
    const swatch = e.target.closest?.('[data-asp-pairs-card] [data-asp-pair-swatch]');
    if (swatch) {
      const card = swatch.closest('[data-asp-pairs-card]');
      card.setAttribute('data-asp-pair-picked', '');
      applyOption(card, swatch);
      renderTotals();
      syncInCartState();
      return;
    }
    if (e.target.matches?.('[data-asp-pairs-check], [data-asp-bundle-perso-check], [data-asp-bundle-gift-check]')) renderTotals();
  });

  document.addEventListener('variant:change', onVariantChange);

  let syncTimer = 0;
  const scheduleSync = () => {
    if (!isOn()) return;
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncInCartState();
    }, 120);
  };

  document.addEventListener('cart:change', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);

  const bindUpcart = () => {
    if (window.__aspPairsUpcartBound) return true;
    if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;
    window.__aspPairsUpcartBound = true;
    window.upcartSubscribeCartUpdated(scheduleSync);
    if (typeof window.upcartSubscribeItemRemoved === 'function') {
      window.upcartSubscribeItemRemoved(scheduleSync);
    }
    if (typeof window.upcartSubscribeCartClosed === 'function') {
      window.upcartSubscribeCartClosed(scheduleSync);
    }
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(scheduleSync);
    }
    return true;
  };
  bindUpcart();
  const upcartWait = window.setInterval(() => {
    if (bindUpcart()) window.clearInterval(upcartWait);
  }, 400);
  window.setTimeout(() => window.clearInterval(upcartWait), 20000);

  document.addEventListener('asp:pairs-enabled', bindAll);

  // Intelligems may set the arm after this script ran: re-sum totals when it flips.
  new MutationObserver(() => renderTotals()).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-asp-bundle-arm']
  });

  const boot = () => {
    try {
      var q = new URLSearchParams(window.location.search).get('asp_pairs');
      if (q === '1' || q === 'true') enablePairsWell();
    } catch (e) {}
    if (isOn()) {
      enablePairsWell();
      bindAll();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.setTimeout(boot, 800);
  window.setTimeout(boot, 2500);

  window.aspGetSelectedPairsItems = selectedItems;
  window.aspPairsSync = syncInCartState;
})();
